/**
 * Recently Visited — tracks the last few pages the user navigated to.
 * Used by MobileNav to surface a quick-access section at the top of the drawer.
 */

const STORAGE_KEY = 'dowurk-recently-visited';
const PINNED_KEY = 'dowurk-pinned-pages';
const VISIT_COUNTS_KEY = 'dowurk-visit-counts';
const SUGGESTION_DISMISSED_KEY = 'dowurk-pin-suggestion-dismissed';
const PIN_SUGGESTED_KEY = 'dowurk-pin-suggested-fired';
const MAX_ENTRIES = 5;
const MAX_PINNED = 8;
const SUGGEST_PIN_THRESHOLD = 3; // visits that trigger the nudge

// Paths we never want to surface as "recent"
const SKIP_PATTERNS = [
  /^\/login/,
  /^\/register/,
  /^\/logout/,
  /^\/forgot-password/,
  /^\/reset-password/,
  /^\/verify-email/,
  /^\/$/, // homepage
  /^\/admin\//, // admin sub-pages (too internal)
];

// Label + style registry — mirrors the labels used in navigation so recent items look consistent
export const ROUTE_LABELS = {
  '/grant-concierge': { label: '🤖 Grant Concierge AI', style: 'text-[#006847] font-semibold hover:bg-[#A4D65E]/20' },
  '/mental-wealth': { label: '🧠 Mental Health · Wealth', style: 'text-emerald-700 font-semibold hover:bg-emerald-50', redirectTo: '/mental-health' },
  '/mental-health/audit': { label: '📋 Founder\'s Honest Audit' },
  '/mental-health/resources': { label: '📖 Mental Health Resources' },
  // Legacy aliases — keep so already-cached labels still resolve
  '/mental-wealth/audit': { label: '📋 Founder\'s Honest Audit', redirectTo: '/mental-health/audit' },
  '/mental-wealth/resources': { label: '📖 Mental Health Resources', redirectTo: '/mental-health/resources' },
  '/compliance-concierge': { label: '🛡️ Stay Right (Compliance)', style: 'text-amber-700 font-semibold hover:bg-amber-50' },
  '/federal-contracting': { label: '🏛️ Federal Contracting', style: 'text-blue-700 font-semibold hover:bg-blue-50' },
  '/sop-builder': { label: '📋 SOP Builder', style: 'text-slate-700 font-semibold hover:bg-slate-50' },
  '/nonprofit-ops': { label: '🏢 Nonprofit Ops', style: 'text-teal-700 font-semibold hover:bg-teal-50' },
  '/capital-readiness': { label: '💰 Capital Readiness', style: 'text-[#388E3C] font-semibold hover:bg-[#A4D65E]/20' },
  '/defi-lending': { label: '🪙 DeFi Lending', style: 'text-emerald-700 font-semibold hover:bg-emerald-50' },
  '/institutional-licensing': { label: '🏛️ Institutional Licensing', style: 'text-indigo-700 font-semibold hover:bg-indigo-50' },
  '/financial-literacy': { label: '📊 Financial Literacy' },
  '/retail-launchpad': { label: '🛍️ Retail Launchpad' },
  '/mentors': { label: '🤝 Mentor Match' },
  '/grants': { label: '💵 Grants & Funding' },
  '/workforce': { label: '💼 wUrkForce Development' },
  '/photography-suite': { label: '📸 Photography Suite' },
  '/real-estate-suite': { label: '🏠 Real Estate Suite' },
  '/restaurant-suite': { label: '🍽️ Restaurant Suite' },
  '/consultant-suite': { label: '💼 Consultant Suite' },
  '/premium/ai-dashboard': { label: '🔒 Premium AI Dashboard', style: 'text-[#1E4D2E] font-semibold' },
  '/ai-generalist': { label: '🎓 AI Generalist Program' },
  '/ai-fundamentals': { label: '🧠 AI Fundamentals' },
  '/ai-curriculum': { label: '📖 AI Training' },
  '/lms': { label: '📚 My Learning' },
  '/ai-assistant': { label: '💬 AI Assistant' },
  '/test-your-knowledge': { label: '🧠 Test Knowledge' },
  '/resources': { label: '📖 Learning Library' },
  '/badges': { label: '🏅 Open Badges' },
  '/businesses': { label: '📋 Business Directory' },
  '/business-map': { label: '🗺️ Business Map' },
  '/service-providers': { label: '🤝 Service Providers' },
  '/capital-marketplace': { label: '💰 Capital Marketplace' },
  '/business-formation': { label: '📝 Formation Wizard' },
  '/permit-lookup': { label: '🔍 Permit Lookup' },
  '/professional-services': { label: '⭐ Professional Services' },
  '/disaster-hub': { label: '🛡️ Disaster Hub' },
  '/veterans': { label: '🎖️ Veterans Support' },
  '/community-feedback': { label: '💬 Send Feedback' },
  '/forum': { label: '👥 Community Forum' },
  '/events': { label: '📅 Events' },
  '/community': { label: '👥 Community Wall' },
  '/faith': { label: '🧭 Faith wUrks' },
  '/fitness': { label: '💪 wUrkFit' },
  '/foundation': { label: '📚 School 4 Thought' },
  '/food': { label: '🍽️ Farm2Table' },
  '/legacystudio': { label: '🎬 LegacyStudio' },
  '/framework': { label: "🌟 Seven F's Framework" },
  '/mental-health': { label: '💚 Mental Health' },
  '/about': { label: 'ℹ️ About' },
  '/our-pledge': { label: '✋ Our Pledge' },
  '/services': { label: '🎯 Services' },
  '/blog': { label: '📰 Blog' },
  '/shop': { label: '🛒 Shop' },
  '/dashboard': { label: '📊 My Dashboard' },
  '/premium': { label: '👑 Premium' },
};

function shouldSkip(path) {
  return SKIP_PATTERNS.some((re) => re.test(path));
}

/**
 * Record a visit. Dedupes by pathname, keeps most-recent first, caps at MAX_ENTRIES.
 * Also increments a per-path visit counter used by the "Suggest pin" nudge.
 * Returns `true` when this visit pushes the count to exactly SUGGEST_PIN_THRESHOLD
 * and the page is neither already pinned nor previously dismissed — caller can then
 * show the suggestion toast.
 */
export function recordVisit(pathname) {
  if (!pathname || shouldSkip(pathname)) return false;
  if (!ROUTE_LABELS[pathname]) return false;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    let list = raw ? JSON.parse(raw) : [];
    list = list.filter((p) => p !== pathname);
    list.unshift(pathname);
    if (list.length > MAX_ENTRIES) list = list.slice(0, MAX_ENTRIES);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));

    // Increment visit count
    const countsRaw = localStorage.getItem(VISIT_COUNTS_KEY);
    const counts = countsRaw ? JSON.parse(countsRaw) : {};
    counts[pathname] = (counts[pathname] || 0) + 1;
    localStorage.setItem(VISIT_COUNTS_KEY, JSON.stringify(counts));

    // Should we suggest a pin? Fire once per page when count first crosses the threshold.
    const suggestedRaw = localStorage.getItem(PIN_SUGGESTED_KEY);
    const suggestedList = suggestedRaw ? JSON.parse(suggestedRaw) : [];
    const alreadyFired = suggestedList.includes(pathname);
    const shouldSuggest =
      counts[pathname] >= SUGGEST_PIN_THRESHOLD &&
      !alreadyFired &&
      !isPinned(pathname) &&
      !isSuggestionDismissed(pathname);
    if (shouldSuggest) {
      suggestedList.push(pathname);
      localStorage.setItem(PIN_SUGGESTED_KEY, JSON.stringify(suggestedList));
    }
    return shouldSuggest;
  } catch {
    return false;
  }
}

export function getVisitCount(pathname) {
  try {
    const raw = localStorage.getItem(VISIT_COUNTS_KEY);
    return raw ? (JSON.parse(raw)[pathname] || 0) : 0;
  } catch {
    return 0;
  }
}

export function isSuggestionDismissed(pathname) {
  try {
    const raw = localStorage.getItem(SUGGESTION_DISMISSED_KEY);
    return raw ? JSON.parse(raw).includes(pathname) : false;
  } catch {
    return false;
  }
}

export function dismissPinSuggestion(pathname) {
  try {
    const raw = localStorage.getItem(SUGGESTION_DISMISSED_KEY);
    const list = raw ? JSON.parse(raw) : [];
    if (!list.includes(pathname)) list.push(pathname);
    localStorage.setItem(SUGGESTION_DISMISSED_KEY, JSON.stringify(list));
  } catch (err) {
    // Storage quota exceeded or disabled — non-critical UX state, log and move on.
    if (process.env.NODE_ENV !== 'production') console.warn('[recentlyVisited] dismissPinSuggestion failed:', err);
  }
}

/**
 * Read list of recent paths (up to MAX_ENTRIES), enriched with label + style.
 * Filters out the current path so the user doesn't see "go where you already are".
 */
export function getRecentVisits(excludePath = null) {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const list = JSON.parse(raw);
    return list
      .filter((p) => p !== excludePath)
      .map((p) => ({ to: p, ...(ROUTE_LABELS[p] || { label: p }) }));
  } catch {
    return [];
  }
}

export function clearRecentVisits() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (err) {
    if (process.env.NODE_ENV !== 'production') console.warn('[recentlyVisited] clearRecentVisits failed:', err);
  }
}


// ==================== PINNED PAGES ====================

/**
 * Read list of pinned paths, enriched with label + style.
 */
export function getPinnedPages() {
  try {
    const raw = localStorage.getItem(PINNED_KEY);
    if (!raw) return [];
    const list = JSON.parse(raw);
    return list.map((p) => ({ to: p, ...(ROUTE_LABELS[p] || { label: p }) }));
  } catch {
    return [];
  }
}

export function isPinned(pathname) {
  try {
    const raw = localStorage.getItem(PINNED_KEY);
    if (!raw) return false;
    return JSON.parse(raw).includes(pathname);
  } catch {
    return false;
  }
}

/**
 * Toggle pin state. Returns the new state (true = now pinned, false = now unpinned).
 * Caps at MAX_PINNED — returns null and does not toggle if cap would be exceeded.
 */
export function togglePin(pathname) {
  if (!pathname || !ROUTE_LABELS[pathname]) return false;
  try {
    const raw = localStorage.getItem(PINNED_KEY);
    let list = raw ? JSON.parse(raw) : [];
    if (list.includes(pathname)) {
      list = list.filter((p) => p !== pathname);
      localStorage.setItem(PINNED_KEY, JSON.stringify(list));
      window.dispatchEvent(new CustomEvent('dowurk:pins-changed'));
      return false;
    }
    if (list.length >= MAX_PINNED) return null; // full
    list.push(pathname);
    localStorage.setItem(PINNED_KEY, JSON.stringify(list));
    window.dispatchEvent(new CustomEvent('dowurk:pins-changed'));
    return true;
  } catch {
    return false;
  }
}

/**
 * True when the current path has a recognizable label and can therefore be pinned.
 */
export function isPinnable(pathname) {
  return !!ROUTE_LABELS[pathname];
}
